import React, { useRef, useState, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { FaCopy, FaFilePdf } from "react-icons/fa";

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------
const BRAND      = "#FEC025";
const BRAND_DARK = "#C9960E";
const DOC_BG     = "#ffffff";
const HEADER_FG  = "#1a1a1a";
const BODY_FG    = "#2d2d2d";
const BORDER     = "#d0d0d0";
const ROW_ALT    = "#fafafa";
const SECTION_CAP = "#3a3a3a";
const FONT = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  // Use UTC methods so date-only strings (parsed as UTC midnight) are not
  // shifted by the browser's local timezone offset (e.g. PST = UTC-8).
  const yyyy = d.getUTCFullYear();
  const mm   = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd   = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SectionHeader = ({ children }) => (
  <div style={{
    background: BRAND,
    borderLeft: `5px solid ${BRAND_DARK}`,
    padding: "9px 14px",
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: "12.5px",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: HEADER_FG,
  }}>
    {children}
  </div>
);

const DocTable = ({ children, style }) => (
  <table style={{ borderCollapse: "collapse", border: "none", width: "100%", fontFamily: FONT, fontSize: "12.5px", ...style }}>
    {children}
  </table>
);

const TH = ({ children, style }) => (
  <th style={{
    background: "#f0f0f0",
    border: "none",
    borderBottom: `1px solid ${BORDER}`,
    borderRight: `1px solid ${BORDER}`,
    padding: "7px 10px",
    fontWeight: 600,
    fontSize: "11.5px",
    color: SECTION_CAP,
    textAlign: "center",
    letterSpacing: "0.3px",
    ...style,
  }}>
    {children}
  </th>
);

const TD = ({ children, center, bold, alt, colSpan, rowSpan, style }) => (
  <td colSpan={colSpan} rowSpan={rowSpan} style={{
    border: "none",
    borderBottom: `1px solid ${BORDER}`,
    borderRight: `1px solid ${BORDER}`,
    padding: "7px 10px",
    verticalAlign: "top",
    backgroundColor: alt ? ROW_ALT : DOC_BG,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    textAlign: center ? "center" : "left",
    fontWeight: bold ? 700 : 400,
    color: BODY_FG,
    fontFamily: FONT,
    fontSize: "12.5px",
    ...style,
  }}>
    {children}
  </td>
);

const StatusBadge = ({ value }) => {
  const isYes = value === "Y" || value === "Yes";
  const isNA  = value === "NA" || value === "N/A";
  const bg    = isYes ? "#d4edda" : isNA ? "#e2e3e5" : "#fff3cd";
  const color = isYes ? "#155724" : isNA ? "#383d41" : "#856404";
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: "3px",
      background: bg,
      color,
      fontWeight: 600,
      fontSize: "11px",
      fontFamily: FONT,
    }}>
      {value || "N"}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Selection table for sections 4-9
// ---------------------------------------------------------------------------
const SelectionTable = ({ groupLabel, areaLabel, items, sectionKey }) => (
  <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
    <DocTable>
      <thead>
        <tr>
          {groupLabel && <TH style={{ textAlign: "left", width: "100px" }}>{groupLabel}</TH>}
          {areaLabel  && <TH style={{ textAlign: "left", width: "90px" }}>{areaLabel}</TH>}
          <TH style={{ textAlign: "left" }}>Selection</TH>
          <TH style={{ width: "70px" }}>Completed</TH>
          <TH>ID Designer Comments</TH>
          <TH style={{ width: "70px" }}>ID Bid Req</TH>
          <TH style={{ width: "60px" }}>PO</TH>
          <TH style={{ width: "110px" }}>PO / Drawing Due</TH>
          <TH>VP Const. Comments</TH>
        </tr>
      </thead>
      <tbody>
        {(items || []).map((item, i) => (
          <tr key={i}>
            <TD alt={i % 2 === 1}>{item.selection}</TD>
            <TD alt={i % 2 === 1} center><StatusBadge value={item.completed} /></TD>
            <TD alt={i % 2 === 1}>{item.interiorDesignerComments}</TD>
            <TD alt={i % 2 === 1} center><StatusBadge value={item.idBidReq} /></TD>
            <TD alt={i % 2 === 1} center><StatusBadge value={item.pO} /></TD>
            <TD alt={i % 2 === 1} center>{formatDate(item.poOrDDue)}</TD>
            <TD alt={i % 2 === 1}>{item.vPSComments}</TD>
          </tr>
        ))}
      </tbody>
    </DocTable>
  </div>
);

// ---------------------------------------------------------------------------
// InteriorDesignPreview
// Props:
//   visible   – boolean
//   onHide    – () => void
//   values    – formik.values snapshot
//   projectName – optional project name string
// ---------------------------------------------------------------------------
const InteriorDesignPreview = ({ visible, onHide, values, projectName }) => {
  const previewRef = useRef(null);
  const toast      = useRef(null);
  const [isCopyLoading, setIsCopyLoading] = useState(false);
  const [isPdfLoading,  setIsPdfLoading]  = useState(false);

  const capturePreview = useCallback(async (pixelRatio = 2) => {
    if (!previewRef.current) return null;
    return await toPng(previewRef.current, { cacheBust: true, pixelRatio, backgroundColor: "#ffffff" });
  }, []);

  const handleCopy = useCallback(async () => {
    setIsCopyLoading(true);
    try {
      const imgSrc = await capturePreview(2);
      if (!imgSrc) { setIsCopyLoading(false); return; }
      const img = new Image();
      img.src = imgSrc;
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
      const canvas = document.createElement("canvas");
      canvas.width = img.width; canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) { setIsCopyLoading(false); return; }
        if (navigator.clipboard?.write) {
          navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })])
            .then(() => toast.current?.show({ severity: "success", summary: "Copied", detail: "Image copied to clipboard" }))
            .catch(() => toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to copy to clipboard" }))
            .finally(() => setIsCopyLoading(false));
        } else {
          toast.current?.show({ severity: "warn", summary: "Unavailable", detail: "Clipboard API not supported" });
          setIsCopyLoading(false);
        }
      }, "image/png");
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to capture preview" });
      setIsCopyLoading(false);
    }
  }, [capturePreview]);

  const handleDownloadPdf = useCallback(async () => {
    setIsPdfLoading(true);
    try {
      const imgSrc = await capturePreview(1.5);
      if (!imgSrc) { setIsPdfLoading(false); return; }
      const img = new Image();
      img.src = imgSrc;
      await new Promise((res) => { img.onload = res; });

      const pdf       = new jsPDF("p", "mm", "a4");
      const pdfW      = pdf.internal.pageSize.getWidth();
      const pdfH      = pdf.internal.pageSize.getHeight();
      const pageCanvH = (pdfH * img.width) / pdfW;

      const full = document.createElement("canvas");
      full.width = img.width; full.height = img.height;
      const fc = full.getContext("2d");
      fc.fillStyle = "#fff"; fc.fillRect(0, 0, full.width, full.height);
      fc.drawImage(img, 0, 0);

      let left = img.height, curY = 0, page = 0;
      while (left > 0) {
        if (page++ > 0) pdf.addPage();
        const sliceH = Math.min(left, pageCanvH);
        const pc = document.createElement("canvas");
        pc.width = img.width; pc.height = sliceH;
        const ctx = pc.getContext("2d");
        ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, pc.width, pc.height);
        ctx.drawImage(full, 0, curY, img.width, sliceH, 0, 0, img.width, sliceH);
        pdf.addImage(pc.toDataURL("image/jpeg", 0.82), "JPEG", 0, 0, pdfW, (sliceH * pdfW) / img.width);
        left -= sliceH; curY += sliceH;
      }

      const now = new Date();
      const fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit",
        day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
      });
      const p = {}; fmt.formatToParts(now).forEach((x) => { p[x.type] = x.value; });
      const ts = `${p.year}-${p.month}-${p.day}_${p.hour}-${p.minute}`;
      const label = projectName ? ` - ${projectName}` : "";
      pdf.save(`Interior Design Weekly Update${label} - ${ts}.pdf`);
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to generate PDF" });
    } finally {
      setIsPdfLoading(false);
    }
  }, [capturePreview, projectName]);

  const dialogFooter = (
    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
      <Button icon={<FaCopy />}    tooltip="Copy to Clipboard" tooltipOptions={{ position: "top" }} loading={isCopyLoading} onClick={handleCopy}       className="dm-btn-copy" type="button" />
      <Button icon={<FaFilePdf />} tooltip="Download PDF"      tooltipOptions={{ position: "top" }} loading={isPdfLoading}  onClick={handleDownloadPdf} className="dm-btn-pdf"  type="button" />
    </div>
  );

  if (!values) return null;

  const name     = projectName || "";
  const weekFrom = formatDate(values.summaryFrom);
  const weekTo   = formatDate(values.summaryTo);
  const weekRange = (weekFrom !== "—" || weekTo !== "—") ? `${weekFrom} — ${weekTo}` : "";

  const DELIVERABLES = [
    { label: "Project Preference Sheet Complete", complete: values.ProjectPreferenceComplete, pct: values.ProjectPreferencePerComplete, comments: values.ProjectPreferenceComments, interior: values.ProjectPreferenceInterior, vpc: values.ProjectPreferenceVPC },
    { label: "RFI's Up to Date",                  complete: values.RFIComplete,               pct: values.RFIPerComplete,               comments: values.RFIComments,               interior: values.RFIInterior,               vpc: values.RFIVPC },
    { label: "Drawings",                          complete: values.DrawingsComplete,           pct: values.DrawingsPerComplete,          comments: values.DrawingsComments,          interior: values.Drawingsinterior,          vpc: values.DrawingsVPC },
    { label: "Selections",                        complete: values.SelectionsComplete,         pct: values.SelectionsPerComplete,        comments: values.SelectionsComments,        interior: values.SelectionsInterior,        vpc: values.SelectionsVPC },
    { label: "Floor Plan",                        complete: values.FLoorPlanComplete,          pct: values.FLoorPlanPerComplete,         comments: values.FLoorPlanComments,         interior: values.FLoorPlanInterior,         vpc: values.FLoorPlanVPC },
  ];

  const BUDGET = [
    { label: "ID Contract Amount", total: values.IDContractAmountTotal, pct: values.IDContractAmountPerComplete, comments: values.IDContractAmountComments, interior: values.IDContractAmountInterior, vpc: values.IDContractAmountVPC },
    { label: "Amount Used",        total: values.AmountUsedTotal,       pct: values.AmountUsedPerComplete,       comments: values.AmountUsedComments,       interior: values.AmountUsedinterior,       vpc: values.AmountUsedVPC },
    { label: "GP %",               total: values.GpPerTotal,           pct: values.GpPerComplete,              comments: values.GpPerComments,            interior: values.GpPerInterior,            vpc: values.GpPerVPC },
  ];

  return (
    <Dialog
      header={`Interior Design Weekly Update Preview${name ? ` — ${name}` : ""}`}
      visible={visible}
      onHide={onHide}
      style={{ width: "96vw", maxWidth: "1280px" }}
      maximizable
      footer={dialogFooter}
      contentStyle={{ overflowY: "auto", padding: "20px", background: "#f2f2f2" }}
    >
      <Toast ref={toast} />

      <div style={{ maxWidth: "1100px", margin: "0 auto", boxShadow: "0 2px 12px rgba(0,0,0,0.10)", borderRadius: "6px", overflow: "hidden" }}>
      <div ref={previewRef} style={{
        background: DOC_BG,
        fontFamily: FONT,
        fontSize: "13px",
        color: BODY_FG,
        width: "1100px",
        boxSizing: "border-box",
      }}>

        {/* Title block */}
        <div style={{
          background: BRAND,
          padding: "28px 32px 22px",
          borderBottom: `4px solid ${BRAND_DARK}`,
        }}>
          <div style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px", textTransform: "uppercase", color: "#7a5a00", marginBottom: "6px", fontFamily: FONT }}>
            Interior Designer Playbook
          </div>
          <div style={{ fontSize: "24px", fontWeight: 800, color: "#1a1a1a", lineHeight: 1.2, marginBottom: "8px", fontFamily: FONT }}>
            Weekly Interior Design Update
          </div>
          {name && (
            <div style={{ fontSize: "16px", fontWeight: 600, color: "#3a2a00", marginBottom: "6px", fontFamily: FONT }}>
              {name}
            </div>
          )}
          {weekRange && (
            <div style={{ fontSize: "12px", color: "#5a4000", fontFamily: FONT }}>
              Period:&nbsp;<strong>{weekRange}</strong>
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "22px" }}>

          {/* Weekly Summary */}
          <div>
            <SectionHeader>Weekly Summary</SectionHeader>
            <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", background: DOC_BG, padding: "16px 18px" }}>
              <div style={{ marginBottom: "10px" }}>
                <span style={{ fontWeight: 600, fontSize: "12px", color: SECTION_CAP, fontFamily: FONT }}>Dear:&nbsp;</span>
                <span style={{ fontSize: "13px", fontFamily: FONT }}>{values.dear || "—"}</span>
              </div>
              {values.dearVPCComments && (
                <div style={{ marginBottom: "10px" }}>
                  <div style={{ fontWeight: 700, fontSize: "11px", color: SECTION_CAP, marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: FONT }}>VP Construction Ops Comments</div>
                  <div style={{ lineHeight: 1.7, fontSize: "13px", whiteSpace: "pre-wrap", color: BODY_FG, padding: "10px 14px", background: ROW_ALT, borderRadius: "4px", border: `1px solid ${BORDER}`, fontFamily: FONT }}>
                    {values.dearVPCComments}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Budget */}
          <div>
            <SectionHeader>Action Steps — Budget</SectionHeader>
            <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
              <DocTable>
                <thead>
                  <tr>
                    <TH style={{ textAlign: "left" }}>Item</TH>
                    <TH style={{ width: "90px" }}>Total</TH>
                    <TH style={{ width: "80px" }}>% Complete</TH>
                    <TH>Comments</TH>
                    <TH style={{ width: "90px" }}>Interior</TH>
                    <TH style={{ width: "90px" }}>VPC</TH>
                  </tr>
                </thead>
                <tbody>
                  {BUDGET.map(({ label, total, pct, comments, interior, vpc }, i) => (
                    <tr key={label}>
                      <TD alt={i % 2 === 1}>{label}</TD>
                      <TD alt={i % 2 === 1} center>{total || "—"}</TD>
                      <TD alt={i % 2 === 1} center>{pct || "—"}</TD>
                      <TD alt={i % 2 === 1}>{comments}</TD>
                      <TD alt={i % 2 === 1} center>{interior}</TD>
                      <TD alt={i % 2 === 1} center>{vpc}</TD>
                    </tr>
                  ))}
                </tbody>
              </DocTable>
            </div>
          </div>

          {/* Deliverables */}
          <div>
            <SectionHeader>Deliverables</SectionHeader>
            <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
              <DocTable>
                <thead>
                  <tr>
                    <TH style={{ textAlign: "left" }}>Item</TH>
                    <TH style={{ width: "80px" }}>Complete</TH>
                    <TH style={{ width: "80px" }}>% Complete</TH>
                    <TH>Comments</TH>
                    <TH style={{ width: "90px" }}>Interior</TH>
                    <TH style={{ width: "90px" }}>VPC</TH>
                  </tr>
                </thead>
                <tbody>
                  {DELIVERABLES.map(({ label, complete, pct, comments, interior, vpc }, i) => (
                    <tr key={label}>
                      <TD alt={i % 2 === 1}>{label}</TD>
                      <TD alt={i % 2 === 1} center><StatusBadge value={complete} /></TD>
                      <TD alt={i % 2 === 1} center>{pct || "—"}</TD>
                      <TD alt={i % 2 === 1}>{comments}</TD>
                      <TD alt={i % 2 === 1} center>{interior}</TD>
                      <TD alt={i % 2 === 1} center>{vpc}</TD>
                    </tr>
                  ))}
                </tbody>
              </DocTable>
            </div>
          </div>

          {/* Pre Start — section_4 */}
          <div>
            <SectionHeader>Pre Start</SectionHeader>
            <SelectionTable items={values.section_4} sectionKey="section_4" />
          </div>

          {/* Schedules — Exterior — section_5 */}
          <div>
            <SectionHeader>Schedules — Exterior</SectionHeader>
            <SelectionTable items={values.section_5} sectionKey="section_5" />
          </div>

          {/* Schedules — Interior — section_6 */}
          <div>
            <SectionHeader>Schedules — Interior</SectionHeader>
            <SelectionTable items={values.section_6} sectionKey="section_6" />
          </div>

          {/* Drawings — Interior — section_8 */}
          <div>
            <SectionHeader>Drawings — Interior</SectionHeader>
            <SelectionTable items={values.section_8} sectionKey="section_8" />
          </div>

          {/* Site Inspections Complete — section_9 */}
          <div>
            <SectionHeader>Site Inspections Complete</SectionHeader>
            <SelectionTable items={values.section_9} sectionKey="section_9" />
          </div>

          {/* Risk / Safety */}
          {values.riskSafety && (
            <div>
              <SectionHeader>Risk / Safety / Client / Team / Other</SectionHeader>
              <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", background: DOC_BG, padding: "14px 18px" }}>
                <div style={{ lineHeight: 1.7, fontSize: "13px", whiteSpace: "pre-wrap", color: BODY_FG, fontFamily: FONT }}>
                  {values.riskSafety}
                </div>
              </div>
            </div>
          )}

          {/* Document footer */}
          <div style={{
            borderTop: `1px solid ${BORDER}`, paddingTop: "12px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            fontSize: "11px", color: "#888", fontFamily: FONT,
          }}>
            <span>Interior Designer Playbook{name ? ` — ${name}` : ""}</span>
            <span>Generated: {formatDate(new Date().toISOString())}</span>
          </div>

        </div>{/* /body */}
      </div>{/* /previewRef */}
      </div>{/* /outer wrapper */}
    </Dialog>
  );
};

export default InteriorDesignPreview;
